<template>
  <div class="main row">
    <div class="col-md-12">
      <KTCodePreview
        v-bind:title="'Chi tiết phiếu đề nghị mua hàng' + ' ' + (pr.code || '')"
      >
        <template v-slot:toolbar>
          <!-- Chỉnh sửa (chờ duyệt, có quyền cập nhật) -->
          <b-button
            v-if="pr.canEdit && checkPermission('PR_UPDATE')"
            variant="primary"
            size="sm"
            class="mr-2"
            @click="editPR"
          >
            <i class="fas fa-edit"></i> Chỉnh sửa
          </b-button>
          <!-- Duyệt / Từ chối / Hoàn duyệt: cờ từ API (canApprove, canReject, canUndo) -->
          <b-button
            v-if="pr.canApprove"
            variant="success"
            size="sm"
            class="mr-2"
            @click="showApproveModal"
          >
            <i class="fas fa-check"></i> Duyệt
          </b-button>
          <b-button
            v-if="pr.canReject"
            variant="danger"
            size="sm"
            class="mr-2"
            @click="showRejectModal"
          >
            <i class="fas fa-times"></i> Từ chối
          </b-button>
          <b-button
            v-if="pr.canUndo"
            variant="warning"
            size="sm"
            class="mr-2"
            @click="showUndoModal"
          >
            <i class="fas fa-undo"></i> Hoàn duyệt
          </b-button>
          <!-- Xoá phiếu (chờ duyệt, người tạo) — API soft-delete, không phải Hủy phiếu -->
          <b-button
            v-if="canDelete && checkPermission('PR_UPDATE')"
            variant="outline-danger"
            size="sm"
            class="mr-2"
            @click="showDeleteModal"
          >
            <i class="fas fa-trash-alt"></i> Xoá phiếu
          </b-button>
          <!-- Lịch sử -->
          <!-- TODO: Implement history modal -->
          <b-button
            variant="secondary"
            size="sm"
            class="mr-2"
            @click="showHistoryModal"
          >
            <i class="flaticon2-time"></i>&nbsp; Lịch sử
          </b-button>
        </template>

        <template v-slot:preview>
          <!-- Thông tin phiếu -->
          <h5 class="mb-3 pr-common-title">Thông tin chung</h5>
          <b-row class="mb-4">
            <b-col cols="3">
              <small class="text-muted">Mã phiếu</small>
              <div>
                <strong>{{ pr.code }}</strong>
              </div>
            </b-col>
            <b-col cols="3">
              <small class="text-muted">Trạng thái</small>
              <div>
                <b-badge :variant="prStatusVariant(pr.status)">{{
                  prStatusName(pr.status)
                }}</b-badge>
              </div>
            </b-col>
            <b-col cols="3">
              <small class="text-muted">Kỳ ngân sách</small>
              <div>{{ formatPeriod(pr.period) }}</div>
            </b-col>
            <b-col cols="3">
              <small class="text-muted">Tổng tiền đề nghị</small>
              <div>
                <strong class="text-primary">{{ convertPrice(pr.totalAmount || pr.requestedAmount) }}</strong>
              </div>
            </b-col>
          </b-row>

          <b-row class="mb-4">
            <b-col cols="3">
              <small class="text-muted">Phòng ban</small>
              <div>{{ pr.departmentName || pr.departmentId }}</div>
            </b-col>
            <b-col cols="3">
              <small class="text-muted">Số dòng</small>
              <div>{{ (pr.lines || []).length }} dòng / {{ pr.storeCount || 0 }} cửa hàng</div>
            </b-col>
          </b-row>

          <!-- ====== LINES TABLE ====== -->
          <h5 class="mb-2 pr-common-title">
            Chi tiết khoản mục phí
            <b-badge variant="info" class="ml-1">{{ (pr.lines || []).length }} dòng</b-badge>
          </h5>
          <PurchaseRequestDetails
            :lines="pr.lines || []"
            :total-amount="pr.totalAmount"
            :requested-amount="pr.requestedAmount"
            class="mb-4"
            @click-budget="showBudgetInfoModal"
            @delete-line="onDeleteLine"
            :can-delete="false"
          />

          <b-row class="mb-4">
            <b-col cols="3">
              <small class="text-muted">Người tạo</small>
              <div>{{ pr.createdByName || pr.createdBy }}</div>
            </b-col>
            <b-col cols="3">
              <small class="text-muted">Ngày tạo</small>
              <div>{{ formatDateTime(pr.createdAt) }}</div>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="4">
              <PurchaseRequestApprovalSummary
                :requestId="pr.id"
                :approvers="pr.approvers"
              />
            </b-col>
          </b-row>

          <b-row class="mb-4">
            <b-col>
              <small class="text-muted">Diễn giải</small>
              <div>{{ pr.description }}</div>
            </b-col>
          </b-row>

          <b-row class="mb-4">
            <b-col cols="8">
              <small class="text-muted d-block mb-1">Tệp đính kèm</small>
              <FileUploadManager
                v-if="pr.id"
                :entity-id="pr.id"
                entity="purchase-requests"
                :disabled="true"
              />
            </b-col>
          </b-row>

          <b-row v-if="pr.rejectionNote" class="mb-4">
            <b-col>
              <strong>Lý do từ chối/hủy:</strong> {{ pr.rejectionNote }}
            </b-col>
          </b-row>
          <PurchaseRequestBudgetModal
            ref="budgetModal"
            :pr="pr"
            :line="selectedBudgetLine"
            :budget-level-tag="budgetLevelTag"
            :budget-level-detail-text="budgetLevelDetailText"
            :budget-used-percent="budgetUsedPercent"
          />
          <h5 class="mb-3 pr-common-title" v-if="pr.hrmFinanceExternalCode">Thông tin phiếu chi</h5>
          <b-row class="mb-4" v-if="pr.hrmFinanceExternalCode">
            <b-col cols="12">
              <b-card class="mb-0 pr-budget-card">
                <b-row class="no-gutters pr-budget-kpi-row">
                  <b-col cols="4" class="pr-2">
                    <div class="pr-budget-kpi-box">
                      <div class="text-muted">Mã phiếu chi</div>
                      <div class="pr-budget-kpi-big">
                        {{ pr.hrmFinanceExternalCode || '--' }}
                      </div>
                    </div>
                  </b-col>
                  <b-col cols="4" class="px-2">
                    <div class="pr-budget-kpi-box">
                      <div class="text-muted">ID phiếu chi</div>
                      <div class="pr-budget-kpi-big">
                        {{ pr.hrmFinanceExternalId || '--' }}
                      </div>
                    </div>
                  </b-col>
                  <b-col cols="4" class="pl-2">
                    <div class="pr-budget-kpi-box pr-budget-kpi-box--remaining">
                      <div class="text-muted">Trạng thái</div>
                      <div class="pr-budget-kpi-big">
                        {{ hrmFinanceExternalStatusName(pr.hrmFinanceExternalStatus) }}
                      </div>
                    </div>
                  </b-col>
                </b-row>
              </b-card>
            </b-col>
          </b-row>

          <PurchaseRequestHistoryModal
            ref="historyModal"
            :histories="histories"
            :fields="historyFields"
          />

          <b-button variant="secondary" @click="goBack">
            <i class="fas fa-arrow-left"></i> Quay lại
          </b-button>

          <!-- Modal duyệt -->
          <b-modal
            ref="approveModal"
            title="Phê duyệt phiếu"
            @ok="confirmApprove"
            ok-variant="success"
            ok-title="Phê duyệt"
            cancel-title="Đóng"
          >
            <b-form-group label="Ghi chú">
              <b-form-textarea
                v-model="approveForm.note"
                rows="2"
                placeholder="Nhập ghi chú duyệt"
              />
            </b-form-group>
          </b-modal>

          <!-- Modal từ chối -->
          <b-modal
            ref="rejectModal"
            title="Từ chối phiếu"
            @ok="confirmReject"
            ok-variant="danger"
            ok-title="Từ chối"
            cancel-title="Đóng"
          >
            <b-form-group>
              <template #label>
                Lý do từ chối <span class="text-danger">*</span>
              </template>
              <b-form-textarea
                v-model="rejectForm.rejectionNote"
                rows="3"
                required
              />
            </b-form-group>
          </b-modal>

          <!-- Modal hoàn duyệt -->
          <b-modal
            ref="undoModal"
            title="Hoàn duyệt"
            @ok="confirmUndo"
            ok-variant="warning"
            ok-title="Hoàn duyệt"
            cancel-title="Đóng"
          >
            <p class="text-muted small mb-2">
              Cấp lại bước duyệt của bạn về chờ duyệt (theo quy tắc quy trình).
            </p>
            <b-form-group label="Ghi chú">
              <b-form-textarea
                v-model="undoForm.note"
                rows="2"
                placeholder="Nhập ghi chú hoàn duyệt"
              />
            </b-form-group>
          </b-modal>

          <b-modal
            ref="deleteModal"
            title="Xoá phiếu đề nghị mua hàng"
            @ok="confirmDelete"
            ok-variant="danger"
            ok-title="Xoá"
            cancel-title="Đóng"
          >
            <p class="mb-0">
              Bạn có chắc muốn xoá phiếu <strong>{{ pr.code }}</strong
              >?
            </p>
          </b-modal>

          <!-- Modal hủy phiếu -->
          <b-modal
            ref="cancelModal"
            title="Hủy phiếu đề nghị mua hàng"
            @ok="confirmCancel"
            ok-variant="danger"
            ok-title="Xác nhận hủy"
            cancel-title="Đóng"
          >
            <p>
              Bạn có chắc muốn hủy phiếu <strong>{{ pr.code }}</strong
              >?
            </p>
            <b-form-group>
              <template #label>
                Lý do hủy <span class="text-danger">*</span>
              </template>
              <b-form-textarea v-model="cancelForm.reason" rows="3" required />
            </b-form-group>
          </b-modal>
        </template>
      </KTCodePreview>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import { checkPermission, getData } from '@/utils/saveDataToLocal';
import {
  makeToastSuccess,
  makeToastFaile,
  convertPrice,
  handleError,
} from '@/utils/common';
import moment from 'moment';
import PurchaseRequestApprovalSummary from './components/PurchaseRequestApprovalSummary.vue';
import PurchaseRequestHistoryModal from './components/PurchaseRequestHistoryModal.vue';
import PurchaseRequestBudgetModal from './components/PurchaseRequestBudgetModal.vue';
import PurchaseRequestDetails from './components/PurchaseRequestDetails.vue';
import FileUploadManager from '@/view/modules/upload/FileUploadManager.vue';
import {
  PR_STATUS,
  PR_STATUS_BADGE_VARIANT,
  APPROVER_STATUS_BADGE_VARIANT,
  PURCHASE_REQUEST_BUDGET_LEVEL,
  PURCHASE_REQUEST_BUDGET_LEVEL_BADGE_VARIANT,
  WORKFLOW_APPROVAL_STEP_STATUS,
} from '@/utils/enum';
import {
  PR_STATUS_NAME,
  APPROVER_STATUS_NAME,
  HISTORY_ACTION_TYPE_CODE,
  PURCHASE_REQUEST_BUDGET_LEVEL_NAME,
} from '@/utils/enum-name';

export default {
  components: {
    KTCodePreview,
    PurchaseRequestApprovalSummary,
    PurchaseRequestHistoryModal,
    PurchaseRequestBudgetModal,
    PurchaseRequestDetails,
    FileUploadManager,
  },
  data() {
    return {
      PR_STATUS,
      pr: {},
      selectedBudgetLine: null,
      histories: [],
      departmentName: '',
      storeName: '',
      currentEmployeeId: null,
      approvalSteps: [],
      approveForm: { note: '' },
      rejectForm: { rejectionNote: '' },
      undoForm: { note: '' },
      cancelForm: { reason: '' },
      approverFields: [
        {
          key: 'step',
          label: 'Bước',
          thStyle: { width: '8%' },
          class: 'text-center',
        },
        {
          key: 'approverBy',
          label: 'Người duyệt',
          thStyle: { width: '25%' },
        },
        {
          key: 'status',
          label: 'Trạng thái',
          thStyle: { width: '15%' },
          class: 'text-center',
        },
        { key: 'note', label: 'Ghi chú' },
        {
          key: 'updatedAt',
          label: 'Thời gian',
          thStyle: { width: '25%' },
          class: 'text-center',
        },
      ],
      historyFields: [
        { key: 'createdAt', label: 'Lúc', thStyle: { width: '15%' } },
        { key: 'actionLabel', label: 'Hành động', thStyle: { width: '15%' } },
        { key: 'description', label: 'Chi tiết' },
        // {
        //   key: 'originalValueText',
        //   label: 'Giá trị cũ',
        //   thStyle: { width: '22%' },
        // },
        // {
        //   key: 'newValueText',
        //   label: 'Giá trị mới',
        //   thStyle: { width: '22%' },
        // },
        {
          key: 'updatedBy',
          label: 'Người thực hiện',
          thStyle: { width: '20%' },
        },
      ],
    };
  },
  computed: {
    /** Chỉ khi phiếu đang chờ duyệt — dùng cho Sửa / Xoá (không dùng cho Duyệt/Từ chối/Hoàn duyệt; các nút đó lấy từ API). */
    isAwaitingApproval() {
      return (
        this.pr.status === PR_STATUS.PENDING ||
        this.pr.status === PR_STATUS.BROWSING
      );
    },
    /** Sửa phiếu khi đang chờ duyệt (backend cho phép PENDING/BROWSING) */
    canEdit() {
      return this.isAwaitingApproval;
    },
    /** Chờ duyệt + người tạo — nút Xoá gọi POST soft-delete, không phải Hủy phiếu */
    canDelete() {
      if (!this.currentEmployeeId || this.pr?.createdBy == null) return false;
      if (!this.isAwaitingApproval) return false;
      return Number(this.pr.createdBy) === Number(this.currentEmployeeId);
    },
    budgetUsedPercent() {
      const allocated = Number(this.pr?.budgetAllocatedAmount);
      const used = Number(this.pr?.budgetUsedAmount);
      if (!allocated || allocated <= 0) return 0;
      const raw = (used / allocated) * 100;
      if (!Number.isFinite(raw)) return 0;
      return Math.max(0, Math.min(100, Math.round(raw)));
    },
    budgetLevelLabel() {
      return (
        PURCHASE_REQUEST_BUDGET_LEVEL_NAME[this.pr.budgetLevel] ||
        this.pr.budgetLevel ||
        ''
      );
    },
    budgetLevelTag() {
      const map = {
        [PURCHASE_REQUEST_BUDGET_LEVEL.STORE]: 'STORE',
        [PURCHASE_REQUEST_BUDGET_LEVEL.DEPARTMENT]: 'DEPARTMENT',
        [PURCHASE_REQUEST_BUDGET_LEVEL.NOT_FOUND]: 'NOT FOUND',
      };
      return map[this.pr.budgetLevel] || String(this.pr.budgetLevel || '');
    },
    budgetLevelDetailText() {
      if (this.pr.budgetLevel === PURCHASE_REQUEST_BUDGET_LEVEL.STORE) {
        return `budget level: ${this.storeName || this.pr.storeId || ''}`;
      }
      if (this.pr.budgetLevel === PURCHASE_REQUEST_BUDGET_LEVEL.DEPARTMENT) {
        return `budget level: ${
          this.departmentName || this.pr.departmentId || ''
        }`;
      }
      if (this.pr.budgetLevel === PURCHASE_REQUEST_BUDGET_LEVEL.NOT_FOUND) {
        return 'budget level: NOT FOUND';
      }
      return this.budgetLevelLabel;
    },
    budgetLevelVariant() {
      return (
        PURCHASE_REQUEST_BUDGET_LEVEL_BADGE_VARIANT[this.pr.budgetLevel] ||
        'secondary'
      );
    },
  },
  created() {
    const id = this.$route.query.id;
    const empInfoRaw = getData('employee_info');
    if (empInfoRaw) {
      try {
        const parsed = JSON.parse(empInfoRaw);
        this.currentEmployeeId = parsed?.id || parsed?._id || null;
      } catch (e) {
        this.currentEmployeeId = null;
      }
    }
    if (id) {
      this.fetchDetail(id);
      this.fetchHistory(id);
    }
    this.fetchLookupData();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Kế toán', route: '#' },
      { title: 'Phiếu đề nghị mua hàng', route: '/budget/purchase-requests' },
      { title: 'Chi tiết' },
    ]);
  },
  methods: {
    checkPermission,
    makeToastSuccess,
    makeToastFaile,
    convertPrice,
    formatPeriod(val) {
      return val ? moment(val).format('MM/YYYY') : '';
    },
    formatDateTime(val) {
      return val ? moment(val).format('DD/MM/YYYY HH:mm') : '';
    },
    formatVnd(val) {
      const n = Number(val);
      if (!Number.isFinite(n)) return '--';
      return convertPrice(n);
    },
    formatShortVnd(val) {
      const n = Number(val);
      if (!Number.isFinite(n)) return '--';
      const abs = Math.abs(n);
      if (abs >= 1e9) return `${this.trimZeros((n / 1e9).toFixed(1))}B`;
      if (abs >= 1e6) return `${this.trimZeros((n / 1e6).toFixed(0))}M`;
      if (abs >= 1e3) return `${this.trimZeros((n / 1e3).toFixed(0))}K`;
      return `${this.trimZeros(n.toFixed(0))}`;
    },
    trimZeros(str) {
      return String(str).replace(/\.0$/, '');
    },
    historyActionLabel(action) {
      return HISTORY_ACTION_TYPE_CODE[action] || String(action || '');
    },
    formatJson(val) {
      if (val === null || val === undefined) return '--';
      if (typeof val === 'string') return val || '--';
      try {
        const str = JSON.stringify(val);
        return str.length > 2000 ? `${str.slice(0, 2000)}...` : str;
      } catch (e) {
        return String(val);
      }
    },
    prStatusVariant(val) {
      return PR_STATUS_BADGE_VARIANT[val] || 'secondary';
    },
    prStatusName(val) {
      return PR_STATUS_NAME[val] || val;
    },
    hrmFinanceExternalStatusName(val) {
      const status = Number(val);
      const map = {
        1: 'Chờ duyệt',
        2: 'Đã duyệt',
        3: 'Không duyệt',
        4: 'Đang duyệt',
        5: 'Nháp',
      };
      return map[status] || '--';
    },
    approverStatusVariant(val) {
      return APPROVER_STATUS_BADGE_VARIANT[val] || 'secondary';
    },
    approverStatusName(val) {
      return APPROVER_STATUS_NAME[val] || val;
    },
    fetchLookupData() {
      // Departments
      ApiService.query('departments/all')
        .then(({ data }) => {
          const rows = data.data || [];
          rows.forEach((d) => {
            if (
              this.pr.departmentId &&
              Number(d.id) === Number(this.pr.departmentId)
            ) {
              this.departmentName = d.name;
            }
          });
        })
        .catch((err) => handleError(err));
      // Stores
      ApiService.query('stores/getStores')
        .then(({ data }) => {
          const rows = data.data || [];
          rows.forEach((s) => {
            if (this.pr.storeId && Number(s.id) === Number(this.pr.storeId)) {
              this.storeName = s.shortName
                ? `${s.name} (${s.shortName})`
                : s.name;
            }
          });
        })
        .catch((err) => handleError(err));
      // Approval config for submit modal
      ApiService.get('v2/approval-configs/by-type/8')
        .then(({ data }) => {
          if (data.status === 200 && data.data) {
            this.mapApprovalSteps(data.data.details || []);
          }
        })
        .catch(() => {
          this.approvalSteps = [];
        });
    },
    mapApprovalSteps(details) {
      this.approvalSteps = (details || [])
        .sort((a, b) => a.step - b.step)
        .map((detail) => {
          const uniqueOwners = [];
          const ownerMap = {};
          (detail.owners || []).forEach((owner) => {
            if (!owner || !owner.id || ownerMap[owner.id]) return;
            ownerMap[owner.id] = true;
            uniqueOwners.push(owner);
          });
          const ownerOptions = [
            { value: null, text: `-- Chọn người duyệt bước ${detail.step} --` },
            ...uniqueOwners.map((owner) => ({
              value: Number(owner.id),
              text: owner.name ? `${owner.name} (${owner.id})` : `${owner.id}`,
            })),
          ];
          return {
            step: detail.step,
            approverBy: null,
            ownerOptions,
          };
        });
    },
    fetchDetail(id) {
      ApiService.get(`v2/purchase-requests/${id}`)
        .then(({ data }) => {
          if (data.status === 200) {
            this.pr = data.data;
            // re-resolve names after pr is loaded
            this.resolvePRNames();
          } else {
            this.makeToastFaile(data.message);
          }
        })
        .catch((err) => handleError(err));
    },
    resolvePRNames() {
      ApiService.query('departments/all')
        .then(({ data }) => {
          const rows = data.data || [];
          const found = rows.find(
            (d) => Number(d.id) === Number(this.pr.departmentId),
          );
          this.departmentName = found ? found.name : '';
        })
        .catch((err) => handleError(err));
      ApiService.query('stores/getStores')
        .then(({ data }) => {
          const rows = data.data || [];
          const found = rows.find(
            (s) => Number(s.id) === Number(this.pr.storeId),
          );
          this.storeName = found
            ? found.shortName
              ? `${found.name} (${found.shortName})`
              : found.name
            : '';
        })
        .catch((err) => handleError(err));
    },
    fetchHistory(id) {
      ApiService.get(`v2/purchase-requests/${id}/history`)
        .then(({ data }) => {
          if (data.status === 200) {
            this.histories = (data.data || []).map((h) => ({
              ...h,
              actionLabel: this.historyActionLabel(h.action),
              originalValueText: this.formatJson(h.originalValue),
              newValueText: this.formatJson(h.newValue),
            }));
          } else {
            this.makeToastFaile(data.message);
          }
        })
        .catch((err) => handleError(err));
    },
    showBudgetInfoModal(line) {
      this.selectedBudgetLine = line || null;
      this.$refs.budgetModal.open();
    },
    onDeleteLine(line) {
      ApiService.post('v2/purchase-requests/delete-line', { id: line.id })
        .then(({ data }) => {
          this.makeToastSuccess(data.message || 'Đã xoá dòng!');
          this.fetchDetail(this.pr.id);
        })
        .catch((err) => handleError(err));
    },
    showHistoryModal() {
      this.$refs.historyModal?.open();
    },
    editPR() {
      this.$router.push({
        name: 'edit-purchase-request',
        query: { id: this.pr.id },
      });
    },
    showApproveModal() {
      this.approveForm.note = '';
      this.$refs.approveModal.show();
    },
    /** Duyệt / hoàn duyệt / từ chối — PATCH change-status/:id (cùng HRM change-status) */
    patchPurchaseRequestChangeStatus(status, note) {
      return ApiService.patch(
        `v2/purchase-requests/change-status/${this.pr.id}`,
        {
          status,
          note: note != null ? String(note) : '',
        },
      );
    },
    confirmApprove() {
      this.patchPurchaseRequestChangeStatus(
        WORKFLOW_APPROVAL_STEP_STATUS.APPROVED,
        this.approveForm.note || '',
      )
        .then(({ data }) => {
          this.makeToastSuccess(data.message);
          this.fetchDetail(this.pr.id);
          this.fetchHistory(this.pr.id);
        })
        .catch((err) => handleError(err));
    },
    showRejectModal() {
      this.rejectForm.rejectionNote = '';
      this.$refs.rejectModal.show();
    },
    showUndoModal() {
      this.undoForm.note = '';
      if (this.$refs.undoModal) this.$refs.undoModal.show();
    },
    confirmUndo() {
      this.patchPurchaseRequestChangeStatus(
        WORKFLOW_APPROVAL_STEP_STATUS.REFUND_APPROVED,
        this.undoForm.note || '',
      )
        .then(({ data }) => {
          this.makeToastSuccess(data.message);
          this.fetchDetail(this.pr.id);
          this.fetchHistory(this.pr.id);
        })
        .catch((err) => handleError(err));
    },
    confirmReject() {
      if (!this.rejectForm.rejectionNote.trim()) {
        this.makeToastFaile('Vui lòng nhập lý do từ chối!');
        return;
      }
      this.patchPurchaseRequestChangeStatus(
        WORKFLOW_APPROVAL_STEP_STATUS.REJECTED,
        this.rejectForm.rejectionNote,
      )
        .then(({ data }) => {
          this.makeToastSuccess(data.message);
          this.fetchDetail(this.pr.id);
          this.fetchHistory(this.pr.id);
        })
        .catch((err) => handleError(err));
    },
    showDeleteModal() {
      if (this.$refs.deleteModal) this.$refs.deleteModal.show();
    },
    confirmDelete() {
      if (!this.pr?.id) return;
      ApiService.post('v2/purchase-requests/soft-delete', { id: this.pr.id })
        .then(({ data }) => {
          this.makeToastSuccess(data.message);
          this.$router.push({ name: 'list-purchase-requests' });
        })
        .catch((err) => handleError(err));
    },
    showCancelModal() {
      this.cancelForm.reason = '';
      this.$refs.cancelModal.show();
    },
    confirmCancel() {
      if (!this.cancelForm.reason.trim()) {
        this.makeToastFaile('Vui lòng nhập lý do hủy!');
        return;
      }
      ApiService.post('v2/purchase-requests/cancel', {
        id: this.pr.id,
        code: this.pr.code,
        reason: this.cancelForm.reason,
      })
        .then(({ data }) => {
          this.makeToastSuccess(data.message);
          this.fetchDetail(this.pr.id);
          this.fetchHistory(this.pr.id);
        })
        .catch((err) => handleError(err));
    },
    goBack() {
      const from = this.$route?.query?.from;
      if (from) {
        this.$router.push(String(from)).catch(() => {});
        return;
      }
      this.$router.push({ name: 'list-purchase-requests' });
    },
  },
};
</script>

<style scoped>
.pr-common-title {
  font-weight: 900;
  color: #111111;
}

.pr-clickable-amount {
  cursor: pointer;
  text-decoration: underline dotted;
}

.pr-clickable-amount:hover {
  opacity: 0.75;
}

/* ── Finance / Budget KPI cards ── */
.pr-budget-card {
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: none;
}

.pr-budget-kpi-box {
  background: #ffffff;
  border-radius: 10px;
  padding: 16px 18px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06);
}

.pr-budget-kpi-box--remaining {
  background: #eafaf4;
}

.pr-budget-kpi-big {
  font-size: 1.2rem;
  font-weight: 900;
  color: #111;
  margin-top: 8px;
  line-height: 1.2;
}
</style>
